"use client";

import { c as _c } from "react-compiler-runtime";
import { useCallback, useEffect, useState } from "react";
import { AgelessAssessmentUsageContext } from "@orval/model";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { useAssessmentUsageContext } from "@/features/assessments/usageContext/useAssessmentUsageContext";
import type { StartCameraOptions } from "@/features/camera/useCameraSession";
type StartCameraFn = (options?: StartCameraOptions) => Promise<boolean>;

/**
 * Where the visitor is relative to the website-embed camera gate:
 * - `checking` — Permissions API lookup in flight; stages show their loading
 *   skeleton (never the tap overlay) so an already-granted camera doesn't
 *   flash a pointless "Enable camera" prompt.
 * - `tap-required` — the browser would need to show a permission prompt (or
 *   cannot tell us), so `getUserMedia` must originate from an in-iframe tap.
 * - `primed` — camera session started (tap consumed or grant pre-existing).
 */
type EmbedCameraGate = "checking" | "tap-required" | "primed";
function supportsCameraPermissionQuery(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.permissions?.query === "function";
}

/**
 * Gates camera start behind an in-iframe tap for website embeds — but only
 * when a browser permission prompt could actually be involved.
 *
 * Why the gate exists: inside a cross-origin embed iframe, a `getUserMedia`
 * permission prompt is only reliably shown when the call carries a user
 * gesture (Safari in particular auto-dismisses gesture-less prompts). Each
 * capture stage (front → profile → lash/brow) mounts its own camera session
 * and this hook with it.
 *
 * Why the gate is permission-aware: the camera stream is fully released
 * between stages, so every stage is a fresh `getUserMedia`. Before this check,
 * every stage re-showed the tap overlay — and visitors read the second tap +
 * browser re-prompt on the profile stage as "it's asking me for permissions
 * again". When `navigator.permissions.query({ name: "camera" })` reports
 * `granted`, starting the camera needs no prompt and therefore no gesture, so
 * the stage auto-starts exactly like the non-embed path. Any other outcome
 * (`prompt`, `denied`, API unsupported — Safari < 16, some WebViews — or the
 * query rejecting) keeps the tap: if the browser insists on prompting again
 * (e.g. an expired one-time grant, or Firefox reporting `prompt` during an
 * active one-time grant), the tap guarantees that prompt carries a gesture.
 */
export function useWebsiteEmbedCameraBootstrap(startCamera) {
  const $ = _c(12);
  const {
    flowType,
    subdomainSlug
  } = useAssessmentStep();
  let t0;
  if ($[0] !== flowType || $[1] !== subdomainSlug) {
    t0 = {
      subdomainSlug,
      flowType
    };
    $[0] = flowType;
    $[1] = subdomainSlug;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    usageContext
  } = useAssessmentUsageContext(t0);
  const isWebsiteEmbed = usageContext === AgelessAssessmentUsageContext.WebsiteEmbed;
  const [gate, setGate] = useState(_temp);
  let t1;
  let t2;
  if ($[3] !== isWebsiteEmbed || $[4] !== startCamera) {
    t1 = () => {
      if (!isWebsiteEmbed) {
        startCamera();
        return;
      }
      if (!supportsCameraPermissionQuery()) {
        return;
      }
      let cancelled = false;
      navigator.permissions.query({
        name: "camera" as PermissionName
      }).then(status => {
        if (cancelled) {
          return;
        }
        if (status.state === "granted") {
          setGate("primed");
          startCamera();
        } else {
          setGate("tap-required");
        }
      }).catch(() => {
        if (!cancelled) {
          setGate("tap-required");
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t2 = [isWebsiteEmbed, startCamera];
    $[3] = isWebsiteEmbed;
    $[4] = startCamera;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== startCamera) {
    t3 = () => {
      setGate("primed");
      startCamera();
    };
    $[7] = startCamera;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const beginEmbedCameraSession = t3;
  const t4 = isWebsiteEmbed && gate === "tap-required";
  let t5;
  if ($[9] !== beginEmbedCameraSession || $[10] !== t4) {
    t5 = {
      needsEmbedCameraTap: t4,
      beginEmbedCameraSession
    };
    $[9] = beginEmbedCameraSession;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp() {
  return supportsCameraPermissionQuery() ? "checking" : "tap-required";
}